import { useQuery, useMutation } from '@apollo/client';
import { Dialog } from '@headlessui/react';
import { IconX } from '@tabler/icons-react';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import * as yup from 'yup';
import { FlavorType, ServerType } from '../../../__generated__/graphql';
import {
  INSTANCE_CREATE_MUTATION,
  PAYLOAD_INSTANCE_CREATE,
} from '../../../api/mutations/instances';
import { FLAVORS_QUERY } from '../../../api/queries/flavors';
import { PAGINATED_INSTANCES_QUERY } from '../../../api/queries/instances';
import { SERVERS_QUERY } from '../../../api/queries/servers';
import { INSTANCE_TYPE, CMS_TYPE, CMS_TYPES } from '../../../constants';
import { useAppDispatch, useAppSelector } from '../../../helpers/reduxHooks';
import {
  updateSelectTheme,
  updateSelectStylesOnError,
  classNames,
} from '../../../helpers/utils';
import { InfoTooltip, LoadingIndicator } from '../../../layout';
import { InfoAlert } from '../../../layout/alerts';
import { Input } from '../../../layout/fields';
import { setSuccessAlert } from '../../../redux/alertSlice';

function onKeyDown(keyEvent: React.KeyboardEvent<HTMLFormElement>) {
  if ((keyEvent.code || keyEvent.key) === 'Enter') {
    keyEvent.preventDefault();
  }
}

const environmentOptions = [
  { value: 'dev', label: 'Development' },
  { value: 'production', label: 'Production' },
];

interface FormikInitialStateType {
  cmsType: SelectOption;
  server: SelectOption | undefined;
  flavor: SelectOption | undefined;
  mainDomain: string;
  siteName: string;
  aliasDomains: SelectOption[];
  redirectionDomains: SelectOption[];
  cloudflareDomains: SelectOption[];
  environment: SelectOption | undefined;
}

interface Props {
  setOverlayState: (value: boolean) => void;
}

function CreateInstanceForm(props: Props) {
  const { setOverlayState } = props;

  const dispatch = useAppDispatch();
  const instancesQueryAttributesState = useAppSelector(
    (state) => state.instancesQueryAttributes,
  );

  const { data: flavorsData } = useQuery(FLAVORS_QUERY);
  const { data: serversData } = useQuery(SERVERS_QUERY, {
    variables: { hideFull: true },
  });

  const [
    createInstanceMutation,
    { data: createInstanceData, loading: createInstanceLoading },
  ] = useMutation(INSTANCE_CREATE_MUTATION, {
    refetchQueries: [
      {
        query: PAGINATED_INSTANCES_QUERY,
        variables: {
          page: instancesQueryAttributesState.page,
          serverDomain: instancesQueryAttributesState.serverDomain,
          name: instancesQueryAttributesState.name,
          category: INSTANCE_TYPE.ACTIVE,
        },
      },
    ],
  });

  const [
    createPayloadInstanceMutation,
    { data: createPayloadInstanceData, loading: createPayloadInstanceLoading },
  ] = useMutation(PAYLOAD_INSTANCE_CREATE, {
    refetchQueries: [
      {
        query: PAGINATED_INSTANCES_QUERY,
        variables: {
          page: instancesQueryAttributesState.page,
          serverDomain: instancesQueryAttributesState.serverDomain,
          name: instancesQueryAttributesState.name,
          category: INSTANCE_TYPE.ACTIVE,
        },
      },
    ],
  });

  const isPayload = (cmsType: SelectOption) =>
    cmsType?.value === CMS_TYPE.PAYLOAD;

  const schema = yup.object({
    cmsType: yup.object().nullable().required('Required'),
    server: yup.object().nullable().required('Required'),
    flavor: yup
      .object()
      .nullable()
      .when('cmsType', {
        is: (cmsType: SelectOption) => !isPayload(cmsType),
        then: (s) => s.required('Required'),
        otherwise: (s) => s.notRequired(),
      }),
    mainDomain: yup.string().required('Required'),
    siteName: yup.string().required('Required'),
    aliasDomains: yup.array(),
    redirectionDomains: yup.array(),
    cloudflareDomains: yup.array(),
    environment: yup
      .object()
      .nullable()
      .when('cmsType', {
        is: (cmsType: SelectOption) => isPayload(cmsType),
        then: (s) => s.required('Required'),
        otherwise: (s) => s.notRequired(),
      }),
  });

  const cmsTypeOptions = CMS_TYPES.map((item) => ({
    value: item.value,
    label: item.name,
  }));

  const {
    handleSubmit,
    handleChange,
    setFieldValue,
    touched,
    values: formikValues,
    errors: formikErrors,
  } = useFormik({
    initialValues: {
      cmsType: cmsTypeOptions[0],
      server: undefined,
      flavor: undefined,
      mainDomain: '',
      siteName: '',
      aliasDomains: [],
      redirectionDomains: [],
      cloudflareDomains: [],
      environment: undefined,
    } as FormikInitialStateType,
    validationSchema: schema,
    onSubmit: (values) => {
      if (isPayload(values.cmsType)) {
        createPayloadInstanceMutation({
          variables: {
            siteName: values.siteName,
            domain: values.mainDomain,
            serverId: values.server?.value,
            environment: values.environment?.value,
          },
        });
      } else {
        const variables = {
          server: values?.server?.value,
          flavor: values?.flavor?.value,
          mainDomain: values.mainDomain,
          siteName: values.siteName,
          aliasDomainsList: values.aliasDomains.map((item) => item.value),
          redirectionDomainsList: values.redirectionDomains.map(
            (item) => item.value,
          ),
          cloudflareDomainsList: values.cloudflareDomains.map(
            (item) => item.value,
          ),
        };
        createInstanceMutation({
          variables,
        });
      }
    },
  });

  useEffect(() => {
    if (flavorsData && !isPayload(formikValues.cmsType)) {
      const options = flavorsData.flavors.response.map((item: FlavorType) => ({
        value: item.id,
        label: item.name,
      }));
      setFieldValue('flavor', options[0]);
    }
  }, [flavorsData, formikValues.cmsType, setFieldValue]);

  useEffect(() => {
    if (isPayload(formikValues.cmsType) && !formikValues.environment) {
      setFieldValue('environment', environmentOptions[0]);
    }
  }, [formikValues.cmsType, formikValues.environment, setFieldValue]);

  useEffect(() => {
    if (serversData) {
      const options = serversData.servers.map((item: ServerType) => ({
        value: item.id,
        label: item.domain,
      }));
      setFieldValue('server', options[0]);
    }
  }, [serversData, setFieldValue]);

  useEffect(() => {
    if (
      createInstanceData &&
      createInstanceData.instanceCreate &&
      createInstanceData.instanceCreate.response
    ) {
      dispatch(
        setSuccessAlert({
          messages: [
            `Deployment of ${formikValues.siteName} has been started.`,
          ],
        }),
      );
      setOverlayState(false);
    }
  }, [createInstanceData, formikValues.siteName, dispatch, setOverlayState]);

  useEffect(() => {
    if (
      createPayloadInstanceData &&
      createPayloadInstanceData.payloadInstanceCreate &&
      createPayloadInstanceData.payloadInstanceCreate.response
    ) {
      dispatch(
        setSuccessAlert({
          messages: [
            `Payload instance ${formikValues.siteName} creation has been started.`,
          ],
        }),
      );
      setOverlayState(false);
    }
  }, [
    createPayloadInstanceData,
    formikValues.siteName,
    dispatch,
    setOverlayState,
  ]);

  const currentServer =
    serversData &&
    formikValues.server &&
    serversData.servers.find(
      (item: ServerType) => item.id === formikValues?.server?.value,
    );

  const isPayloadSelected = isPayload(formikValues.cmsType);

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={onKeyDown}
      className="flex h-full flex-col divide-y divide-slate-200  bg-white shadow-xl"
    >
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="bg-slate-700 py-4 px-4 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-lg font-medium text-white">
              New {isPayloadSelected ? 'Payload' : 'MODX'} Instance
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="rounded-md bg-slate-700 text-slate-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                onClick={() => setOverlayState(false)}
              >
                <span className="sr-only">Close panel</span>
                <IconX className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-slate-200 px-4 sm:px-6">
            <div className="space-y-6 pt-6 pb-5">
              <div>
                <label className="block text-sm font-bold text-slate-900">
                  CMS Type
                  <span className="mr-1 text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Select
                    value={formikValues.cmsType}
                    name="cmsType"
                    options={cmsTypeOptions}
                    isSearchable={false}
                    onChange={(e) => setFieldValue('cmsType', e)}
                    theme={(theme) => updateSelectTheme(theme)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900">
                  Instance Server
                  <span className="mr-1 text-red-500">*</span>
                </label>
                <div className="mt-1">
                  {serversData && serversData.servers.length ? (
                    <Select
                      value={formikValues.server}
                      name="server"
                      options={serversData.servers.map((item: ServerType) => ({
                        value: item.id,
                        label: item.domain,
                      }))}
                      isSearchable={false}
                      onChange={(e) => setFieldValue('server', e)}
                      theme={(theme) => updateSelectTheme(theme)}
                      //@ts-ignore
                      styles={formikErrors.server && updateSelectStylesOnError}
                    />
                  ) : (
                    <LoadingIndicator />
                  )}
                </div>
              </div>
              {formikValues.server && currentServer && (
                <InfoAlert
                  message={`${currentServer.instancesLeft} Instance(s) more can be created on this server`}
                />
              )}

              {isPayloadSelected && (
                <div>
                  <label className="block text-sm font-bold text-slate-900">
                    Environment <span className="mr-1 text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <Select
                      value={formikValues.environment}
                      name="environment"
                      options={environmentOptions}
                      isSearchable={false}
                      onChange={(e) => setFieldValue('environment', e)}
                      theme={(theme) => updateSelectTheme(theme)}
                      //@ts-ignore
                      styles={
                        formikErrors.environment && updateSelectStylesOnError
                      }
                    />
                  </div>
                </div>
              )}

              {!isPayloadSelected && (
                <div>
                  <label className="block text-sm font-bold text-slate-900">
                    Instance Flavor <span className="mr-1 text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    {flavorsData && flavorsData.flavors.response.length ? (
                      <Select
                        value={formikValues.flavor}
                        name="flavor"
                        options={flavorsData.flavors.response.map(
                          (item: FlavorType) => ({
                            value: item.id,
                            label: item.name,
                          }),
                        )}
                        className="flavor-select"
                        classNamePrefix="select"
                        isSearchable={false}
                        onChange={(e) => setFieldValue('flavor', e)}
                        theme={(theme) => updateSelectTheme(theme)}
                        //@ts-ignore
                        styles={
                          formikErrors.flavor && updateSelectStylesOnError
                        }
                      />
                    ) : (
                      <LoadingIndicator />
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-slate-900">
                  Site Name <span className="mr-1 text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Input
                    placeholder="Example: Zeller & Co"
                    name="siteName"
                    value={formikValues.siteName}
                    //@ts-ignore
                    errorMessage={touched.siteName && formikErrors.siteName}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="flex text-sm font-bold text-slate-900">
                  Main Domain <span className="mr-1 text-red-500">*</span>
                  {formikValues.server && (
                    <InfoTooltip
                      text={`Main Domain without a subdomain will automatically be extended with "${formikValues.server.label}" suffix`}
                    />
                  )}
                </label>
                <div className="mt-1">
                  <Input
                    placeholder="Example: zeller03.de"
                    name="mainDomain"
                    value={formikValues.mainDomain}
                    //@ts-ignore
                    errorMessage={touched.mainDomain && formikErrors.mainDomain}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {!isPayloadSelected && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-900">
                      Alias Domains
                    </label>
                    <div className="mt-1">
                      <CreatableSelect
                        isMulti
                        isClearable
                        className="mt-1"
                        placeholder='Type in domain and press "Tab"'
                        onChange={(e) =>
                          e
                            ? setFieldValue('aliasDomains', e)
                            : setFieldValue('aliasDomains', [])
                        }
                        theme={(theme) => updateSelectTheme(theme)}
                        components={{
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null,
                          Menu: () => null,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-900">
                      Redirection Domains
                    </label>
                    <div className="mt-1">
                      <CreatableSelect
                        isMulti
                        isClearable
                        className="mt-1"
                        placeholder='Type in domain and press "Tab"'
                        onChange={(e) =>
                          e
                            ? setFieldValue('redirectionDomains', e)
                            : setFieldValue('redirectionDomains', [])
                        }
                        theme={(theme) => updateSelectTheme(theme)}
                        components={{
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null,
                          Menu: () => null,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="flex text-sm font-medium text-slate-900">
                      Cloudflare-proxied Domains
                      <InfoTooltip
                        text="List any Main/Alias/Redirection domain above that is proxied through Cloudflare (orange cloud). DNS validation will skip the origin IP check for these, and a manually uploaded Origin CA certificate will be used instead of Let's Encrypt."
                      />
                    </label>
                    <div className="mt-1">
                      <CreatableSelect
                        isMulti
                        isClearable
                        className="mt-1"
                        placeholder='Type in domain and press "Tab"'
                        onChange={(e) =>
                          e
                            ? setFieldValue('cloudflareDomains', e)
                            : setFieldValue('cloudflareDomains', [])
                        }
                        theme={(theme) => updateSelectTheme(theme)}
                        components={{
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null,
                          Menu: () => null,
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          type="button"
          className="rounded-md border border-slate-300 bg-white py-2 px-4 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
          onClick={() => setOverlayState(false)}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={classNames(
            createPayloadInstanceLoading || createInstanceLoading
              ? 'bg-slate-400 cursor-not-allowed'
              : 'bg-slate-600 hover:bg-slate-700',
            'ml-4 inline-flex justify-center rounded-md border border-transparent py-2 px-4 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2',
          )}
          disabled={createPayloadInstanceLoading || createInstanceLoading}
        >
          {createPayloadInstanceLoading || createInstanceLoading ? (
            <>
              <LoadingIndicator className="mr-2 h-4 w-4 text-white" />
              {isPayloadSelected ? 'Creating...' : 'Deploying...'}
            </>
          ) : isPayloadSelected ? (
            'Create'
          ) : (
            'Deploy'
          )}
        </button>
      </div>
    </form>
  );
}

export default CreateInstanceForm;
